import { Link, type LinkProps } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { useLabelValueVariant } from './LabelValueGroup'
import { cn, FOCUS_RING } from '@/components/base'
import { GROUP_LABEL_CLASS } from '@/components/GroupLabel'
import { PANEL_FRAME_CLASS } from '@/components/Internal/Panel'

/**
 * A figure under its caption — the shape every overview strip repeats.
 *
 * Inside a `LabelValueGroup variant="tiles"` it is a tile: the caption small
 * and uppercase, the figure large, `hint` under it and `status` — a `Badge`
 * saying whether the figure is good news — beside the caption. `to` makes the
 * whole tile the way to what the figure counts.
 *
 * Outside a group, `size="sm"` is the compact pair a list row or a fact sheet
 * carries: caption faint, figure at the row's size, `align="end"` for a
 * column of numbers.
 */
export function LabelValue({
  label,
  value,
  valueClassName,
  hint,
  status,
  to,
  size = 'md',
  align = 'start',
  className,
}: {
  label: string
  value: ReactNode
  valueClassName?: string
  /** Tiles only: what the figure is made of, or what it compares against. */
  hint?: ReactNode
  /** Tiles only. */
  status?: ReactNode
  /** Tiles only. */
  to?: LinkProps
  /** Not in tiles. */
  size?: 'md' | 'sm'
  /** Not in tiles: a figure sits at the end of its column. */
  align?: 'start' | 'end'
  /** Not in tiles: the pair's own box, where the row sizes it. */
  className?: string
}) {
  if (useLabelValueVariant() !== 'tiles') {
    const small = size === 'sm'
    return (
      <div
        className={cn(
          'flex flex-col',
          align === 'end' && 'items-end',
          className,
        )}
      >
        <span
          className={
            small ? 'text-[10.5px] text-faint' : 'text-sm text-muted-foreground'
          }
        >
          {label}
        </span>
        <span
          className={cn(
            small ? 'text-[13px] text-foreground' : 'font-medium',
            'tabular-nums',
            valueClassName,
          )}
        >
          {value}
        </span>
      </div>
    )
  }

  const body = (
    <>
      <span className="flex flex-row items-center justify-between gap-2">
        <span className={cn(GROUP_LABEL_CLASS, 'truncate')}>{label}</span>
        {status}
      </span>
      <span
        className={cn(
          'text-2xl font-semibold tabular-nums text-foreground',
          valueClassName,
        )}
      >
        {value}
      </span>
      {hint ? (
        <span className="line-clamp-2 text-xs text-muted-foreground">
          {hint}
        </span>
      ) : null}
    </>
  )
  const tile = cn(PANEL_FRAME_CLASS, 'flex min-w-0 flex-col gap-1 px-4 py-3')

  return to ? (
    <Link
      {...to}
      className={cn(
        tile,
        'transition-colors hover:border-primary/40',
        FOCUS_RING,
      )}
    >
      {body}
    </Link>
  ) : (
    <div className={tile}>{body}</div>
  )
}
